/* Global search / command palette — the PURE index + ranking (W3-H).
 *
 * The command palette (ui/commandPalette.ts) searches across what the hub
 * legitimately has loaded client-side TODAY — no new bulk endpoints, no
 * full-content pretense:
 *   - clients: the shared GET /clients roster (views/register.ts).
 *   - tasks:   the cross-client GET /me/tasks projection (lib/myWork.ts) — the
 *              signed-in AM's own standing-relation work, already fetched at
 *              boot for the rail badge. This is the HONEST cross-client task
 *              set; full-content search across every client's board lands with
 *              the server FTS service (per-client FTS exists at
 *              /clients/{slug}/tasks?q= but there is no global endpoint).
 *   - screens: the routable surfaces — the four rail destinations plus every
 *              client's cockpit deep pages ("El Al › Search").
 *
 * Every function here is pure and unit-tested (tests/unit/searchIndex.test.ts);
 * the UI module owns the DOM. Task hrefs reuse ws/taskLink.buildTaskHref — the
 * ONE shared task deep-link grammar — so this never re-invents it.
 */
import { buildTaskHref } from '../ws/taskLink.ts';

export type SearchKind = 'client' | 'task' | 'screen';

export interface SearchEntry {
  /** stable unique id (for keys + dedupe). */
  id: string;
  kind: SearchKind;
  /** primary display line. */
  title: string;
  /** secondary display line — honest, derived, never fabricated. */
  subtitle: string;
  /** deep link on select. A `#/…` hash (client/screen) or a full `/review/?…`
   * app URL (task, via buildTaskHref). navKind tells the UI how to follow it. */
  href: string;
  navKind: 'hash' | 'assign';
  /** lowercased searchable text (title + synonyms + context). */
  haystack: string;
  /** the primary name field, boosted in scoring (brand / screen label). */
  primary: string;
}

export interface SearchGroup {
  kind: SearchKind;
  label: string;
  entries: SearchEntry[];
}

/** The minimal client shape the index needs (a subset of RegisterClient). */
export interface IndexClient {
  slug: string;
  brand: string;
  descriptor?: string | null;
  domain?: string | null;
  lastRunFile?: string | null;
}

/** The minimal task shape the index needs (a subset of MyWorkRow). */
export interface IndexTask {
  task_id: number;
  client_slug: string;
  brand: string;
  title: string;
  status: string;
}

/** A rail destination (global screen). */
interface GlobalScreen {
  key: string;
  label: string;
  href: string;
  blurb: string;
  syn: string;
}

/** A cockpit deep page, relative to a client. `seg` is the router path segment
 * (`''` = the Overview tab, `#/client/{slug}`). */
interface CockpitPage {
  seg: string;
  label: string;
  blurb: string;
  syn: string;
}

const GLOBAL_SCREENS: readonly GlobalScreen[] = [
  { key: 'clients', label: 'Clients', href: '#/clients', blurb: 'the client book', syn: 'client book roster register accounts' },
  { key: 'work', label: 'My Work', href: '#/work', blurb: 'your cross-client inbox', syn: 'inbox tasks assigned watching mentions triage' },
  { key: 'team', label: 'Team', href: '#/team', blurb: 'the account-management rollup', syn: 'department people members workload' },
  { key: 'prospects', label: 'Prospects', href: '#/prospects', blurb: 'the BizDev pipeline', syn: 'bizdev pipeline pre-client leads' },
];

const COCKPIT_PAGES: readonly CockpitPage[] = [
  { seg: '', label: 'Overview', blurb: 'presence map & reputation', syn: 'summary presence reputation home entity' },
  { seg: 'board', label: 'Tasks', blurb: 'the client board', syn: 'tasks board work ledger kanban' },
  { seg: 'search', label: 'Search', blurb: 'IMPACT search results', syn: 'serp google search results impact page-1 ownership' },
  { seg: 'ai', label: 'AI narrative', blurb: 'what AI engines say', syn: 'ai narrative llm chatgpt gemini reputation aiq' },
  { seg: 'news', label: 'News', blurb: 'press & media', syn: 'news press media coverage' },
  { seg: 'wiki', label: 'Wikipedia', blurb: 'Wikipedia & community', syn: 'wikipedia community reference wikidata' },
  { seg: 'plan', label: 'Plan', blurb: 'the intelligence plan', syn: 'plan strategy program roadmap' },
  { seg: 'requests', label: 'Requests', blurb: 'client requests', syn: 'requests intake asks' },
  { seg: 'deliverables', label: 'Deliverables', blurb: 'client deliverables', syn: 'deliverables output work orders' },
  { seg: 'activity', label: 'Activity', blurb: 'the change history', syn: 'activity history log timeline' },
];

function statusLabel(status: string): string {
  switch (status) {
    case 'in-progress': return 'In progress';
    case 'waiting-client': return 'Waiting on client';
    case 'waiting-platform': return 'Waiting on platform';
    case 'implemented': return 'Implemented';
    case 'regressed': return 'Regressed';
    case 'wont-do': return "Won't do";
    default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Open';
  }
}

function clientEntry(c: IndexClient): SearchEntry {
  const descriptor = c.descriptor || 'Client record';
  return {
    id: `client:${c.slug}`,
    kind: 'client',
    title: c.brand,
    subtitle: descriptor,
    href: `#/client/${c.slug}`,
    navKind: 'hash',
    haystack: `${c.brand} ${descriptor} ${c.domain ?? ''} ${c.slug}`.toLowerCase(),
    primary: c.brand,
  };
}

function taskEntry(t: IndexTask, runFile?: string | null): SearchEntry {
  return {
    id: `task:${t.client_slug}:${t.task_id}`,
    kind: 'task',
    title: t.title,
    subtitle: `${t.brand} · ${statusLabel(t.status)}`,
    href: buildTaskHref({ clientSlug: t.client_slug, taskId: t.task_id, runFile }),
    navKind: 'assign',
    haystack: `${t.title} ${t.brand} ${statusLabel(t.status)} ${t.status}`.toLowerCase(),
    primary: t.title,
  };
}

function globalScreenEntry(s: GlobalScreen): SearchEntry {
  return {
    id: `screen:g:${s.key}`,
    kind: 'screen',
    title: s.label,
    subtitle: s.blurb,
    href: s.href,
    navKind: 'hash',
    haystack: `${s.label} ${s.blurb} ${s.syn}`.toLowerCase(),
    primary: s.label,
  };
}

function cockpitPageEntry(c: IndexClient, p: CockpitPage): SearchEntry {
  return {
    id: `screen:c:${c.slug}:${p.seg || 'overview'}`,
    kind: 'screen',
    title: `${c.brand} › ${p.label}`,
    subtitle: p.blurb,
    href: `#/client/${c.slug}${p.seg ? `/${p.seg}` : ''}`,
    navKind: 'hash',
    haystack: `${c.brand} ${p.label} ${p.blurb} ${p.syn} ${c.slug}`.toLowerCase(),
    primary: `${c.brand} ${p.label}`,
  };
}

/** Build the full flat search index from the loaded roster + task set. Clients
 * change infrequently and N is small, so building all entries (incl. every
 * cockpit deep page) is cheap; ranking bounds what the UI shows. */
export function buildIndex(clients: readonly IndexClient[], tasks: readonly IndexTask[]): SearchEntry[] {
  const runFileBySlug = new Map(clients.map((c) => [c.slug, c.lastRunFile ?? null]));
  const out: SearchEntry[] = [];
  for (const c of clients) out.push(clientEntry(c));
  for (const t of tasks) out.push(taskEntry(t, runFileBySlug.get(t.client_slug)));
  for (const s of GLOBAL_SCREENS) out.push(globalScreenEntry(s));
  for (const c of clients) for (const p of COCKPIT_PAGES) out.push(cockpitPageEntry(c, p));
  return out;
}

/** Free text → lowercased alnum tokens (unicode-aware), mirroring the server's
 * fts `_fts_query` tokenization so client-side and server search agree on what
 * a "word" is. */
export function tokenize(q: string): string[] {
  return (q.toLowerCase().match(/[^\W_]+/gu) ?? []).filter(Boolean);
}

/** Score one entry against the query tokens. Returns -1 for a non-match (a
 * token that appears nowhere). AND semantics: every token must hit. Higher is
 * better; the kind boost breaks ties so clients out-rank screens out-rank the
 * long tail of cockpit pages. Pure — the ranking unit tests pin this. */
export function scoreEntry(entry: SearchEntry, tokens: string[]): number {
  if (!tokens.length) return 0;
  const hay = entry.haystack;
  const words = hay.split(/\s+/).filter(Boolean);
  const primary = entry.primary.toLowerCase();
  let score = 0;
  for (const tok of tokens) {
    if (!hay.includes(tok)) return -1; // AND: an unmatched token disqualifies
    if (words.some((w) => w.startsWith(tok))) score += 6; // word-boundary prefix
    else score += 2; // mid-word substring
  }
  const joined = tokens.join(' ');
  if (primary === joined) score += 60; // exact name match
  else if (primary.startsWith(joined)) score += 30; // name prefix
  score += entry.kind === 'client' ? 5 : entry.kind === 'task' ? 3 : 1;
  return score;
}

const GROUP_LABEL: Record<SearchKind, string> = {
  client: 'Clients',
  task: 'Your tasks',
  screen: 'Screens',
};
const GROUP_ORDER: readonly SearchKind[] = ['client', 'task', 'screen'];
/** Per-group result caps — keep the palette scannable, never a wall. */
const GROUP_CAP: Record<SearchKind, number> = { client: 6, task: 8, screen: 7 };

/** The default (empty-query) suggestion set: the global screens + the first few
 * clients, so an opened-but-untyped palette is a useful jump list, never blank.
 * Cockpit deep pages and tasks stay out of the default — they surface on query. */
export function defaultGroups(index: readonly SearchEntry[]): SearchGroup[] {
  const clients = index.filter((e) => e.kind === 'client').slice(0, GROUP_CAP.client);
  const screens = index.filter((e) => e.id.startsWith('screen:g:'));
  const groups: SearchGroup[] = [];
  if (clients.length) groups.push({ kind: 'client', label: GROUP_LABEL.client, entries: clients });
  if (screens.length) groups.push({ kind: 'screen', label: GROUP_LABEL.screen, entries: screens });
  return groups;
}

/** Rank + group the index for a query. Empty query → defaultGroups. Non-empty →
 * scored, filtered, capped, grouped in Clients · Your tasks · Screens order. */
export function search(index: readonly SearchEntry[], query: string): SearchGroup[] {
  const tokens = tokenize(query);
  if (!tokens.length) return defaultGroups(index);

  const scored = index
    .map((entry) => ({ entry, score: scoreEntry(entry, tokens) }))
    .filter((r) => r.score >= 0)
    .sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title));

  const groups: SearchGroup[] = [];
  for (const kind of GROUP_ORDER) {
    const entries = scored.filter((r) => r.entry.kind === kind).slice(0, GROUP_CAP[kind]).map((r) => r.entry);
    if (entries.length) groups.push({ kind, label: GROUP_LABEL[kind], entries });
  }
  return groups;
}

/** Flatten grouped results into the keyboard-navigation order (the same
 * top-to-bottom order they render). */
export function flatten(groups: readonly SearchGroup[]): SearchEntry[] {
  return groups.flatMap((g) => g.entries);
}
